import React, { useMemo } from 'react';
import { Card, Box, Typography } from '@mui/material';
import { COLOR, FUENTE } from './tokens';
import InfoGrafico from './InfoGrafico';
import { MOCK_VOLUMEN_CONVERSION } from './mockDataEjemplos';

/**
 * Volumen de turnos vs. conversión a trámite por tipo de trámite. La barra
 * representa el volumen (escala 0..max) y se colorea entera según si la conversión
 * de esa fila está sobre o bajo el promedio general — sin punto aparte, ya que el
 * texto lateral ya dice el % exacto. Implementado con divs (no Chart.js) para
 * alinear con precisión barra + texto por fila.
 * @param {{data: Array<{tipoTramite: string, totalTurnos: number, conTramite: number, tasaConversion: number}>}} props
 */
const TramitesVolumenConversion = ({ data, compacto = false }) => {
    const filas = useMemo(() => [...(data || [])].sort((a, b) => b.totalTurnos - a.totalTurnos), [data]);
    const maxTurnos = useMemo(() => Math.max(1, ...filas.map((f) => f.totalTurnos)), [filas]);

    // Promedio general ponderado (no promedio simple de los % por fila) — mismo
    // criterio que conversionGlobal en selectors.js, para no calcular dos veces con
    // dos criterios distintos lo que en el fondo es el mismo número.
    const promedioGeneral = useMemo(() => {
        const totalTurnos = filas.reduce((acc, f) => acc + (f.totalTurnos || 0), 0);
        const conTramite = filas.reduce((acc, f) => acc + (f.conTramite || 0), 0);
        return totalTurnos ? (conTramite / totalTurnos) * 100 : 0;
    }, [filas]);

    return (
        <Card variant="outlined" sx={{
            position: compacto ? 'static' : 'relative', border: compacto ? 'none' : '1px solid #e2e8f0',
            borderRadius: compacto ? 0 : '12px', boxShadow: 'none', padding: compacto ? 0 : '20px 22px',
        }}>
            {!compacto && (
                <>
                    <InfoGrafico
                        titulo="Volumen vs. conversión"
                        queMuestra="Te dice, para cada tipo de turno, cuántas atenciones se finalizaron y en qué porcentaje se abrió un trámite — para detectar dónde hay mucho volumen pero poco resultado."
                        ejemploVisual={<TramitesVolumenConversion compacto data={MOCK_VOLUMEN_CONVERSION} />}
                        comoLeerlo={[
                            'El largo de la barra es la cantidad de atenciones finalizadas de ese tipo — no es la conversión. Los que no vinieron no cuentan (nunca tuvieron la oportunidad), ni los turnos que no son atención al público (cobros, turnos médicos...) ni quien vino por un trámite que ya tenía en curso. Es la misma base de la conversión del encabezado.',
                            'Se agrupa por el tipo de TURNO (con qué consulta vino la persona). Por eso no coincide con "Trámites iniciados por tipo", que agrupa por el módulo del trámite que se abrió: un turno de Jubilación puede terminar en un Plan de Pago.',
                            'Verde = la conversión de ese tipo está en o por encima del promedio general ponderado de todos los tipos (5.3% en este ejemplo).',
                            'Rojo = la conversión de ese tipo está por debajo de ese promedio general.',
                            'A la derecha, "Conversión" es el % exacto y "Trámites" la cantidad concreta de turnos que abrieron un trámite.',
                        ]}
                        analisis="ART es el tipo con más volumen después de RTI (90 turnos) pero su conversión (3.3%) está bien por debajo del promedio (5.3%) — es la barra roja más grande del gráfico, así que es la que más vale la pena revisar primero. GENERAL, en cambio, tiene poco volumen (30) y conversión nula: puede ser una categoría que en la práctica funciona como 'consulta', no como origen real de trámites."
                    />
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 16, fontWeight: 700, color: COLOR.negro }}>Volumen vs. conversión</Typography>
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto, marginBottom: '16px' }}>
                        Agrupado por <b>tipo de turno</b> (la consulta con la que vino la persona). No coincide fila a fila
                        con «Trámites iniciados por tipo», que agrupa por el trámite que terminó abriéndose (un turno de
                        Jubilación puede terminar en un Plan de pago). «Trámites» cuenta atenciones en las que se abrió al
                        menos un trámite. Verde/rojo: sobre o bajo el promedio general ({promedioGeneral.toFixed(1)}%).
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', paddingBottom: '4px' }}>
                        <Box sx={{ width: 110, flexShrink: 0 }} />
                        <Box sx={{ flex: 1 }} />
                        <Box sx={{ width: 160, flexShrink: 0, display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <Typography sx={{ fontFamily: FUENTE, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: COLOR.grisTexto, width: 84, textAlign: 'right' }}>
                                Conversión
                            </Typography>
                            <Typography sx={{ fontFamily: FUENTE, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: COLOR.grisTexto, width: 64, textAlign: 'right' }}>
                                Trámites
                            </Typography>
                        </Box>
                    </Box>
                </>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: compacto ? '8px' : '14px' }}>
                {filas.map((f) => {
                    const anchoBarra = (f.totalTurnos / maxTurnos) * 100;
                    const sobrePromedio = Number(f.tasaConversion || 0) >= promedioGeneral;
                    const colorBarra = sobrePromedio ? COLOR.verde : COLOR.rojo;
                    return (
                        <Box key={f.tipoTramite} sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.negro, width: 110, flexShrink: 0 }}>
                                {f.tipoTramite}
                            </Typography>
                            <Box sx={{ position: 'relative', flex: 1, height: 18 }}>
                                <Box sx={{
                                    position: 'absolute', inset: 0, borderRadius: '4px', background: COLOR.gris,
                                }} />
                                <Box sx={{
                                    position: 'absolute', top: 0, bottom: 0, left: 0, width: `${anchoBarra}%`,
                                    borderRadius: '4px', background: colorBarra,
                                }} />
                            </Box>
                            <Box sx={{ width: 160, flexShrink: 0, display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                <Typography sx={{ fontFamily: FUENTE, fontSize: 13, fontWeight: 700, color: colorBarra, fontVariantNumeric: 'tabular-nums', width: 84, textAlign: 'right' }}>
                                    {Number(f.tasaConversion || 0).toFixed(1)}%
                                </Typography>
                                <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: COLOR.grisTexto, fontVariantNumeric: 'tabular-nums', width: 64, textAlign: 'right' }}>
                                    {(f.conTramite || 0).toLocaleString('es-AR')}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
                {!compacto && filas.length > 0 && (
                    // Total de atenciones con trámite. Coincide con el total de «Trámites iniciados por tipo»
                    // salvo cuando una atención abrió más de un trámite (allá cuenta cada trámite).
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', borderTop: '1px solid #E3E6EB', paddingTop: '10px' }}>
                        <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, fontWeight: 700, color: COLOR.negro, width: 110, flexShrink: 0 }}>
                            Total
                        </Typography>
                        <Typography sx={{ flex: 1, fontFamily: FUENTE, fontSize: 12, color: COLOR.grisTexto }}>
                            {filas.reduce((acc, f) => acc + (f.totalTurnos || 0), 0).toLocaleString('es-AR')} atenciones finalizadas
                        </Typography>
                        <Box sx={{ width: 160, flexShrink: 0, display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <Typography sx={{ fontFamily: FUENTE, fontSize: 13, fontWeight: 700, color: COLOR.negro, fontVariantNumeric: 'tabular-nums', width: 84, textAlign: 'right' }}>
                                {promedioGeneral.toFixed(1)}%
                            </Typography>
                            <Typography sx={{ fontFamily: FUENTE, fontSize: 13, fontWeight: 700, color: COLOR.negro, fontVariantNumeric: 'tabular-nums', width: 64, textAlign: 'right' }}>
                                {filas.reduce((acc, f) => acc + (f.conTramite || 0), 0).toLocaleString('es-AR')}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        </Card>
    );
};

export default TramitesVolumenConversion;
